import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

// A `position: fixed` overlay is sized to the layout viewport, which the
// on-screen keyboard does not shrink (iOS Safari, Chrome Android ≥ 108). The
// bottom of the dialog then sits under the keyboard and its own scroll ends
// there, so the last rows can never be reached. Pinning the overlay to the
// visual viewport keeps the whole dialog inside what the user actually sees.
export function useVisualViewportBox(): CSSProperties | undefined {
  const [box, setBox] = useState<CSSProperties | undefined>(undefined);

  useEffect(() => {
    const viewport = typeof window === 'undefined' ? undefined : window.visualViewport;
    if (!viewport) {
      return undefined;
    }

    function update(): void {
      if (!viewport) {
        return;
      }

      setBox({ top: viewport.offsetTop, height: viewport.height, bottom: 'auto' });
    }

    function onResize(): void {
      update();
      revealFocusedField();
    }

    update();
    viewport.addEventListener('resize', onResize);
    viewport.addEventListener('scroll', update);

    return () => {
      viewport.removeEventListener('resize', onResize);
      viewport.removeEventListener('scroll', update);
    };
  }, []);

  return box;
}

function revealFocusedField(): void {
  const focused = document.activeElement;
  if (!(focused instanceof HTMLElement) || focused === document.body) {
    return;
  }

  if (typeof focused.scrollIntoView === 'function') {
    focused.scrollIntoView({ block: 'nearest' });
  }
}
